import i18n from '../../../i18n/config';
import { ORG_PERSON_NODE_TYPES } from '../constants/orgNodeTypes';

export type OrgChartViewMode = 'compact' | 'full';
export type OrgPersonCardVariant = 'photo' | 'text' | 'card';

export const DEFAULT_ORG_CHART_VIEW_MODE: OrgChartViewMode = 'compact';
export const DEFAULT_ORG_PERSON_SHAPE_TYPE = 'text' as const;
export const DEFAULT_ORG_PERSON_CARD_VARIANT: OrgPersonCardVariant = 'text';
export const DEFAULT_ORG_PERSON_NODE_TYPE = 'org_person_text' as const;

export function isOrgPersonNodeType(type: string | null | undefined): boolean {
  return typeof type === 'string' && ORG_PERSON_NODE_TYPES.has(type);
}

export function resolveOrgChartViewMode(
  mode: string | null | undefined,
): OrgChartViewMode {
  return mode === 'full' ? 'full' : DEFAULT_ORG_CHART_VIEW_MODE;
}

export function resolveOrgPersonCardVariant(
  variant: string | null | undefined,
): OrgPersonCardVariant {
  if (variant === 'photo' || variant === 'card' || variant === 'text') return variant;
  return DEFAULT_ORG_PERSON_CARD_VARIANT;
}

export function orgPersonNodeTypeFromCardVariant(
  variant: OrgPersonCardVariant,
): string {
  switch (variant) {
    case 'photo':
      return 'org_person_photo';
    case 'card':
      return 'org_person_photo_inner_left';
    default:
      return DEFAULT_ORG_PERSON_NODE_TYPE;
  }
}

export function orgPersonCardVariantFromNodeType(
  nodeType: string | null | undefined,
): OrgPersonCardVariant {
  if (nodeType === 'org_person_text') return 'text';
  if (nodeType === 'org_person_photo_inner_left') return 'card';
  if (isOrgPersonNodeType(nodeType)) return 'photo';
  return DEFAULT_ORG_PERSON_CARD_VARIANT;
}

export function toDefaultOrgPersonNodeType(
  type: string | null | undefined,
): string {
  return isOrgPersonNodeType(type) ? DEFAULT_ORG_PERSON_NODE_TYPE : (type ?? DEFAULT_ORG_PERSON_NODE_TYPE);
}

/** Localized defaults for new org shapes (non-React callers use i18n directly). */
export function getOrgChartDefaultPersonName(): string {
  return i18n.t('diagram:orgchart.defaultPersonName', { defaultValue: 'New Person' }) as string;
}

export function getOrgChartDefaultRole(): string {
  return i18n.t('diagram:orgchart.defaultRole', { defaultValue: 'Employee' }) as string;
}

export function getOrgChartDefaultPositionTitle(): string {
  return i18n.t('diagram:orgchart.defaultPositionTitle', { defaultValue: 'New Position' }) as string;
}

export function getOrgChartDefaultDepartmentName(): string {
  return i18n.t('diagram:orgchart.defaultDepartmentName', { defaultValue: 'New Department' }) as string;
}

export function getOrgChartDefaultManagerLabel(): string {
  return i18n.t('diagram:orgchart.defaultManagerLabel', { defaultValue: 'Manager' }) as string;
}

export function getOrgChartDefaultNewManagerTitle(): string {
  return i18n.t('diagram:orgchart.defaultNewManagerTitle', { defaultValue: 'New Manager' }) as string;
}
